import { Luggage, Person, Timeline, WifiOff } from '@mui/icons-material'
import {
  Alert,
  Box,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material'
import JobNumberAudit from 'components/Bookings/Audit/JobNumberAudit'
import DisplayPrice from 'components/Common/DisplayValues/DisplayPrice'
import VehicleDisplayName from 'components/Common/VehicleDisplayName/VehicleDisplayName'
import ScrollFadeBox from 'components/UI/ScrollFadeBox'
import StatusChip from 'components/UI/StatusChip'
import useUserRole from 'hooks/useUserRole'
import { useCallback, useEffect, useState } from 'react'
import AmendVehicleCard from './AmendVehicleCard'
import VehicleDetailsModal from './AmendVehicleDetailsModal'

const VehicleJobs = ({
  currentBooking,
  quotedJobs,
  editStates,
  setEditState,
  updatePreparedAmend,
  selectedVehicle,
  setSelectedVehicle,
  setChosenVehicle,
}: any) => {
  const [amendVehicleModal, setAmendVehicleModal] = useState<boolean>(false)

  // Only set the selected vehicle; effect below will open modal
  const handleSelectVehicle = useCallback(
    (v: any) => {
      setSelectedVehicle(v)
    },
    [setSelectedVehicle],
  )

  useEffect(() => {
    if (selectedVehicle && !amendVehicleModal) {
      setAmendVehicleModal(true)
    }
  }, [selectedVehicle, amendVehicleModal])

  useEffect(() => {
    if (!editStates.vehicle) {
      setAmendVehicleModal(false)
      setSelectedVehicle(null)
    }
  }, [editStates.vehicle, setSelectedVehicle])

  const handleCloseModal = useCallback(() => {
    setAmendVehicleModal(false)
    setSelectedVehicle(null)
  }, [setSelectedVehicle])

  const { hasRole } = useUserRole()
  return editStates.vehicle ? (
    <>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <ScrollFadeBox
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          {(quotedJobs?.quotes ?? []).length > 0 ? (
            quotedJobs.quotes.map((v: any) => {
              return (
                <AmendVehicleCard
                  key={v.supplierQuoteReference}
                  vehicle={v}
                  setSelectedVehicle={handleSelectVehicle}
                  clientId={currentBooking?.booking.client.clientId}
                  selectedVehicle={selectedVehicle}
                  updatePreparedAmend={updatePreparedAmend}
                  setAmendVehicleModal={() => {}}
                  // Ensure card cannot overflow
                  sx={{ flex: '1 1 320px', maxWidth: '360px', width: '100%' }}
                />
              )
            })
          ) : (
            <Alert severity="warning">
              No vehicles are currently available, please check the date, time,
              pickup / drop off locations, the required number of passengers.
            </Alert>
          )}
        </ScrollFadeBox>
      </Box>

      {selectedVehicle && (
        <VehicleDetailsModal
          data={{
            title: selectedVehicle?.vehicleDetails.displayName || '',
            example: selectedVehicle?.vehicleDetails.example || '',
            cardist: selectedVehicle?.vehicleDetails.description || '',
            image: selectedVehicle?.vehicleDetails.icon || '',
            seats: selectedVehicle?.vehicleDetails.seats || '',
            luggage: selectedVehicle?.vehicleDetails.luggage || '',
            quotes: selectedVehicle || {},
          }}
          openModal={amendVehicleModal}
          setOpenModal={handleCloseModal}
          isImmediateJourney={false}
          isFlexible={quotedJobs}
          displayVATPrice={currentBooking?.booking.client.displayVAT}
          selectedVehicles={selectedVehicle}
          currentBooking={currentBooking}
          setSelectedVehicles={setChosenVehicle}
          numOfVehicle={0}
          quoteReference={quotedJobs?.quoteRef}
          isOneWay={true}
          isAsDirected={false}
          setNumOfVehicle={() => {}}
          handleBookCar={updatePreparedAmend}
          setEditState={setEditState}
        />
      )}
    </>
  ) : (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          height: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', paddingY: '6px' }}>
          <List
            sx={{
              width: '100%',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              maxHeight: '20vh',
            }}
          >
            {currentBooking &&
              currentBooking?.jobs.map((job: any, index: number) => {
                return (
                  <Grid key={index}>
                    <ListItem alignItems="center">
                      <Box
                        component="img"
                        src={job.vehicleDetails.icon}
                        sx={{ width: '12%', marginLeft: '10px' }}
                      />
                      <ListItemText
                        sx={{
                          marginLeft: '30px',
                        }}
                        primary={
                          <>
                            <Box
                              sx={{
                                display: 'flex',
                                gap: '4px',
                                alignItems: 'center',
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 'bold',
                                  textTransform: 'uppercase',
                                  fontSize: '1.2rem',
                                }}
                              >
                                {VehicleDisplayName({
                                  vehicleDetails: job.vehicleDetails,
                                })}
                                : {job.jobReference}
                              </Typography>

                              <Tooltip title="View Audit">
                                <IconButton
                                  sx={{ display: 'inline' }}
                                  aria-label="history"
                                  onClick={() =>
                                    JobNumberAudit({
                                      jobNumber: job?.id,
                                      bookingId: job?.bookingId,
                                      fixedPriceDisplay: false,
                                      displayVATPrice: false,
                                    })
                                  }
                                >
                                  <Timeline />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </>
                        }
                        secondary={
                          <>
                            <Box
                              sx={{
                                display: 'flex',
                                gap: '5px',
                                alignItems: 'center',
                                paddingBottom: '5px',
                              }}
                            >
                              <>
                                <Typography>
                                  {job.supplier.companyName}
                                </Typography>
                                {hasRole([
                                  'Jyrney_Admin',
                                  'Platform_Admin',
                                  'Client_Admin',
                                ]) && (
                                  <>
                                    <Typography>
                                      {job.supplier.supplierReference &&
                                        `(${job.supplier.supplierReference})`}
                                    </Typography>
                                    {job.supplier.isOffline && (
                                      <Tooltip title="Offline Supplier">
                                        <WifiOff
                                          fontSize="small"
                                          sx={{ m: 0, p: 0 }}
                                        />
                                      </Tooltip>
                                    )}
                                  </>
                                )}
                              </>
                              <StatusChip
                                status={
                                  job.status.progress ?? job.status.status
                                }
                                statusType={
                                  job.status.progress
                                    ? 'VehicleJobProgress'
                                    : 'VehicleJobStatus'
                                }
                              />
                            </Box>

                            <Box
                              sx={{
                                display: 'flex',
                                gap: '10px',
                                paddingTop: '3px',
                              }}
                            >
                              <Typography sx={{ display: 'flex', gap: '3px' }}>
                                {job.actualPrice ? '' : 'Est.'}
                                {DisplayPrice({
                                  VATTotalPrice:
                                    job.actualPrice ?? job.estimatedPrice,
                                  displayVATPrice:
                                    currentBooking?.booking.client.displayVAT,
                                })}
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{
                                  display: 'flex',
                                  gap: '4px',
                                  alignItems: 'center',
                                }}
                              >
                                <Person />
                                {`${job.vehicleDetails.seats}`}
                                <Luggage />
                                {`${job.vehicleDetails.luggage}`}
                              </Typography>
                            </Box>
                            {hasRole([
                              'Jyrney_Admin',
                              'Platform_Admin',
                              'Client_Admin',
                              'Travel_Consultant',
                              'Senior_Travel_Consultant',
                            ]) && (
                              <ListItemText
                                primary={`Invoice Ref. ${
                                  job.invoiceReference
                                    ? `${job.invoiceReference}`
                                    : 'TBC'
                                }`}
                                sx={{ paddingTop: '5px' }}
                              />
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  </Grid>
                )
              })}
          </List>
        </Box>
      </Box>
    </>
  )
}

export default VehicleJobs
